import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader";
import { DRACOLoader } from "three/examples/jsm/loaders/DRACOLoader.js";
import { KTX2Loader } from "./lib/KTX2Loader.js";
import { BasisTextureLoader } from "./lib/BasisTextureLoader";
import {
  Scene,
  PerspectiveCamera,
  RepeatWrapping,
  TextureLoader,
  Mesh,
  Vector3,
  ShaderMaterial,
  Material,
  PMREMGenerator,
  LoadingManager,
  Object3D,
  BufferGeometry,
  Matrix4,
  MeshBasicMaterial,
  sRGBEncoding,
  InstancedMesh,
  DoubleSide,
  AnimationMixer,
  Color,
  NearestFilter,
  LinearFilter,
  LinearMipmapLinearFilter,
  SphereBufferGeometry,
  FogExp2,
} from "three";
import { Water } from "./lib/Water";
import CameraControls from "./CameraControls";
import waterNormalsSrc from "url:../maps/waternormals.jpg";
import { ActionType, World } from "../contexts/AppContext";
import WorldAudioScene from "./WorldAudioScene";
import Easing from "easing-functions";
import {
  disposeComplete,
  loaderUrlModifierForTextures,
  setTextureProperties,
} from "../util/three-utils";
import { USE_BASIS_TEXTURES } from "../App";
import DeviceGradeDetection, {
  DeviceGrade,
} from "../util/DeviceGradeDetection";
import GPUParticles from "./GPUParticles/GPUParticles";
import AmbientParticles from "./AmbientParticles";
import RingEmitterParticles from "./RingEmmitterParticles";
import OuterScene from "./OuterScene";
import GPUGrass from "./GPUGrass/GPUGrass";
import SkyShaderMaterial, {
  SkyShaderParams,
} from "../shaders/SkyShaderMaterial/SkyShader";

interface InstanceData {
  material: Material;
  geometry: BufferGeometry;
  children: Mesh[];
}

const HIDDEN_MATERIAL = new MeshBasicMaterial({
  transparent: true,
  opacity: 0,
  wireframe: false,
  color: 0x00ff00,
  side: DoubleSide,
  // depthWrite: true,
  // depthTest: false,
});

export default class WorldScene extends Scene {
  private modelSrc: string;
  private lookSrcs: string[];
  private plantSrcs: string[];
  private instancedMeshes: string[];
  public camera: PerspectiveCamera = new PerspectiveCamera(
    75,
    window.innerWidth / window.innerHeight,
    0.1,
    666
  );
  private cameraControls?: CameraControls;
  private loadingManager = new LoadingManager();
  private gltfLoader = new GLTFLoader(this.loadingManager);
  private textureLoader = new TextureLoader(this.loadingManager);
  private basisLoader = new KTX2Loader(this.loadingManager);
  private dracoLoader = new DRACOLoader(this.loadingManager);
  private water?: Mesh;
  private gpuGrass?: GPUGrass;
  private audioScene?: WorldAudioScene;
  private isDisposed = false;
  private mixer?: AnimationMixer;
  private gpuParticles?: GPUParticles;
  private ambientParticles?: AmbientParticles;
  private ringParticles: RingEmitterParticles[] = [];
  private sky?: Mesh;

  constructor(
    world: World,
    modelSrc: string,
    lookSrcs: string[] = [],
    plantSrcs: string[] = [],
    instancedMeshes: string[] = [],
    skyConfig: SkyShaderParams
  ) {
    super();

    this.dracoLoader.setDecoderPath("libs/draco/");
    this.dracoLoader.setDecoderConfig({ type: "js" });
    this.gltfLoader.setDRACOLoader(this.dracoLoader);

    if (USE_BASIS_TEXTURES) {
      this.basisLoader.setTranscoderPath("libs/basis/");
      this.basisLoader.detectSupport((window as any).webGlView.renderer);
      this.loadingManager.addHandler(/\.(jpg|jpeg|png)$/i, this.basisLoader);
    }

    this.modelSrc = modelSrc;
    this.lookSrcs = lookSrcs;
    this.plantSrcs = plantSrcs;
    this.instancedMeshes = instancedMeshes;

    if (DeviceGradeDetection.deviceGrade > DeviceGrade.Low) {
      this.gpuParticles = new GPUParticles({
        maxParticles:
          DeviceGradeDetection.isMobile ||
          DeviceGradeDetection.deviceGrade !== DeviceGrade.High
            ? 100000
            : 200000,
      });
      this.add(this.gpuParticles);
    }

    if (world === World.FungiForest && this.gpuParticles) {
      this.ambientParticles = new AmbientParticles(
        this.gpuParticles,
        this.camera
      );
    }

    this.sky = new Mesh(
      new SphereBufferGeometry(500),
      new SkyShaderMaterial(skyConfig)
    );
    this.add(this.sky);

    this.loadingManager.onProgress = (url, itemsLoaded, itemsTotal) => {
      (window as any).dispatch({
        type: ActionType.LoadProgress,
        value: itemsLoaded / itemsTotal,
      });
    };
    this.loadingManager.setURLModifier(loaderUrlModifierForTextures);

    // this.overrideMaterial = new MeshBasicMaterial({
    //   color: 0xff0000,
    //   wireframe: true,
    // });
  }

  public async init() {
    const toLoad = [this.modelSrc, ...this.lookSrcs, ...this.plantSrcs]; // the world scene must be first in the loading array;
    const models = await Promise.all(
      toLoad.map(async (src) => {
        return await this.gltfLoader.loadAsync(src);
      })
    );

    if (this.isDisposed) return;
    // Convert to instanced meshes
    if (this.instancedMeshes.length > 0) {
      const toInstance = new Map<string, InstanceData>();
      models[0].scene.traverse((child) => {
        if (child instanceof Mesh) {
          this.instancedMeshes.forEach((instanceName) => {
            const regex = new RegExp(`${instanceName}\\d+`);
            if (regex.test(child.name)) {
              if (toInstance.get(instanceName)) {
                const updatedInstanceData = { ...toInstance.get(instanceName) };
                updatedInstanceData.children.push(child);
                toInstance.set(instanceName, updatedInstanceData);
              } else {
                toInstance.set(instanceName, {
                  children: [child],
                  material: child.material,
                  geometry: child.geometry,
                });
              }
            }
          });
        }
      });

      toInstance.forEach((instanceData, name) => {
        const instancedMesh = new InstancedMesh(
          instanceData.geometry,
          instanceData.material,
          instanceData.children.length
        );
        instancedMesh.name = name;
        instanceData.children.forEach((child, index) => {
          child.parent.remove(child);
          const matrix = new Matrix4();
          matrix.compose(child.position, child.quaternion, child.scale);
          instancedMesh.setMatrixAt(index, matrix);
        });
        models[0].scene.add(instancedMesh);
        instancedMesh.instanceMatrix.needsUpdate = true;
        instanceData.geometry.dispose();
        instanceData.material.dispose();
        console.info(`Created InstancedMesh for ${name}`);
      });
    }

    // add particles
    if (
      DeviceGradeDetection.deviceGrade > DeviceGrade.Medium &&
      this.gpuParticles
    ) {
      models[0].scene.traverse((child) => {
        if (child.name.indexOf("UI_World_") === 0) {
          const ringEmitter = new RingEmitterParticles(
            this.gpuParticles,
            this.camera,
            {
              spawnRate: 2500,
              activeDistance: 10,
              radius: 0.87,
              colour: new Color(0x33eb40),
              spread: 0.15,
              velocityRandomness: 0.075,
              getLifespan: () => Math.random() * 4,
              getSpeed: () => 1 + Math.random() * 0.5,
            }
          );
          child.getWorldPosition(ringEmitter.position);
          this.ringParticles.push(ringEmitter);
          this.add(ringEmitter);
          child.visible = false;
        } else if (
          child.name.indexOf("Placeholder_Plant") === 0 ||
          child.name.indexOf("Placeholder_Look") === 0
        ) {
          const ringEmitter = new RingEmitterParticles(
            this.gpuParticles,
            this.camera,
            {
              spawnRate: 800,
              activeDistance: 5,
              radius: 0.8,
              colour: new Color(0xffffff),
              spread: 0.25,
              velocityRandomness: 0.15,
              getLifespan: () => Easing.Quintic.In(Math.random()) * 5,
              getSpeed: () => 3 + Math.random() * 1,
            }
          );
          child.getWorldPosition(ringEmitter.position);
          this.ringParticles.push(ringEmitter);
          this.add(ringEmitter);
        } else if (child.name.indexOf("Placeholder_Manifesto") === 0) {
          const ringEmitter = new RingEmitterParticles(
            this.gpuParticles,
            this.camera,
            {
              spawnRate: 2500,
              activeDistance: 5,
              radius: 0.6,
              colour: new Color(0x33eb40),
              spread: 0.25,
              velocityRandomness: 0.15,
              getLifespan: () => Easing.Quintic.In(Math.random()) * 4,
              getSpeed: () => 4 + Math.random() * 2,
            }
          );
          child.getWorldPosition(ringEmitter.position);
          this.ringParticles.push(ringEmitter);
          this.add(ringEmitter);
        }
      });
    }

    // Add models from placeholders
    models.forEach((model, index) => {
      if (index === 0) {
        this.add(model.scene);
        return;
      }
      const placeholderLook = this.getObjectByName(
        `Placeholder_Look_${model.scene.name}`
      ) as Mesh;
      if (placeholderLook) {
        (placeholderLook.material as Material).dispose();
        placeholderLook.material = HIDDEN_MATERIAL;
        model.scene.position.copy(placeholderLook.position);
        model.scene.rotation.copy(placeholderLook.rotation);
        this.add(model.scene);
      }

      const placeholderPlant = this.getObjectByName(
        `Placeholder_Plant_${model.scene.name}`
      ) as Mesh;
      if (placeholderPlant) {
        (placeholderPlant.material as Material).dispose();
        placeholderPlant.material = HIDDEN_MATERIAL;
        model.scene.position.copy(placeholderPlant.position);
        model.scene.rotation.copy(placeholderPlant.rotation);
        this.add(model.scene);
      }
    });

    // nothing in the scenes we've added so far moves so we can improve performance by setting matrixAutoUpdate to false
    // IMPORTANT: if we want something to move, we want this to be set to true
    this.traverse((child: Object3D) => {
      child.updateMatrix();
      child.matrixAutoUpdate = false;
    });
    // Add animations
    if (
      models[0].animations[0] &&
      DeviceGradeDetection.deviceGrade > DeviceGrade.Low
    ) {
      this.mixer = new AnimationMixer(models[0].scene);
      models[0].animations.forEach((animation) => {
        animation.tracks.forEach((track) => {
          // because we've set matrixAutoUpdate to false, we have to find any animated object and set matrixAutoUpdate to true
          const objectName = track.name.split(".")[0];
          const object = this.getObjectByName(objectName);
          if (object) {
            object.matrixAutoUpdate = true;
          }
        });
        this.mixer.clipAction(animation).play();
      });
    } else {
    }

    // add water
    const waterObject = this.getObjectByName("Water") as Mesh;
    if (waterObject) {
      const waterGeometry = waterObject.geometry.clone();
      waterGeometry.rotateX(Math.PI / 2); // rotation bug is very strange, have to have the geometry vertical and then rotate the water back to be flat.
      const mapWaterNormals = await this.textureLoader.loadAsync(
        waterNormalsSrc
      );
      mapWaterNormals.wrapS = mapWaterNormals.wrapT = RepeatWrapping;
      this.water = new Water(waterGeometry, {
        textureWidth: window.innerWidth <= 768 ? 512 : 1024,
        textureHeight: window.innerWidth <= 768 ? 512 : 1024,
        waterNormals: mapWaterNormals,
        sunDirection: new Vector3(),
        sunColor: 0xffffff,
        // waterColor: 0x00302e,
        waterColor: 0x69a9b3,
        distortionScale: 0.3,
        fog: true,
      });
      this.fog = new FogExp2(
        this.sky.material.uniforms.uColourHorizon.value,
        0.025
      );

      this.water.position.copy(waterObject.position);
      this.water.rotation.copy(waterObject.rotation);
      this.water.scale.copy(waterObject.scale);
      this.water.rotation.x += -Math.PI / 2;
      ((waterObject as Mesh).material as Material).dispose();
      (waterObject as Mesh).geometry.dispose();
      waterObject.parent.remove(waterObject);

      (this.water.material as ShaderMaterial).uniforms["size"].value = 1;
      this.add(this.water);

      (this.water.material as ShaderMaterial).uniforms[
        "mirrorSampler"
      ].value.encoding = sRGBEncoding;
      (this.water.material as ShaderMaterial).uniforms[
        "normalSampler"
      ].value.encoding = sRGBEncoding;
    }

    const grassObject = this.getObjectByName("Placeholder_GPU-Grass") as Mesh;
    if (DeviceGradeDetection.deviceGrade > DeviceGrade.Medium && grassObject) {
      this.gpuGrass = new GPUGrass({
        density: DeviceGradeDetection.isMobile ? 14 : 20,
        geometry: grassObject.geometry,
        camera: this.camera,
        renderer: (window as any).webGlView.renderer,
      });
      this.gpuGrass.position.copy(grassObject.position);
      this.gpuGrass.scale.copy(grassObject.scale);
      this.gpuGrass.rotation.copy(grassObject.rotation);
      await this.gpuGrass.init();
      this.add(this.gpuGrass);
      grassObject.material = new MeshBasicMaterial({
        color: 0xff0000,
        transparent: true,
        opacity: 0.7,
      });
      grassObject.parent.remove(grassObject);
      console.info("Adding GPU Grass");
    }

    const modelSky = this.getObjectByName("Sky");
    if (modelSky) {
      modelSky.parent.remove(modelSky);
    }

    setTextureProperties(this, {
      encoding: sRGBEncoding,
      magFilter: LinearFilter,
      minFilter: LinearMipmapLinearFilter,
      anisotropy: (
        window as any
      ).webGlView.renderer.capabilities.getMaxAnisotropy(),
    });

    // this.audioScene = new WorldAudioScene();

    this.add(this.camera);
    this.cameraControls = new CameraControls(
      this.camera,
      document.querySelector("canvas"),
      this.getObjectByName("Camera_Start")
    );

    const hills = this.getObjectByName("Hills-Distance") as Mesh;
    if (hills) {
      hills.renderOrder = -1;
    }

    const pmremGenerator = new PMREMGenerator(
      (window as any).webGlView.renderer
    );
    let outerScene = new OuterScene();
    this.environment = pmremGenerator.fromScene(
      outerScene.sky as any as Scene
    ).texture;
    pmremGenerator.dispose();
    disposeComplete(outerScene);
    outerScene = null;
  }

  public enableControls(): void {
    this.cameraControls?.enable();
  }

  public disableControls(): void {
    this.cameraControls?.disable();
  }

  public async dispose(): Promise<boolean> {
    this.isDisposed = true;
    this.dracoLoader.dispose();
    this.basisLoader.dispose();
    this.audioScene?.dispose();
    this.cameraControls?.dispose();
    this.loadingManager.onProgress = null;
    this.gpuParticles?.dispose();
    disposeComplete(this);
    return true;
  }

  public update(delta: number): void {
    this.cameraControls?.update(delta);
    this.mixer?.update((1 / 60) * delta);
    if (this.water) {
      (this.water.material as ShaderMaterial).uniforms["time"].value +=
        (0.1 / 60.0) * delta * 0.5;
    }
    (this.sky?.material as SkyShaderMaterial).update(delta);
    this.gpuGrass?.update(delta);
    this.gpuParticles?.update(delta);
    this.ambientParticles?.update(delta, this.cameraControls.speed);
    this.ringParticles.forEach((ring) => ring.update(delta));
  }

  public onResize(width: number, height: number): void {
    this.camera.aspect = width / height;
    this.camera.updateProjectionMatrix();
  }
}
